import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StepHeader, RadioCard, OnboardingButton } from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import type { BusinessType } from '@/types/onboarding'
import { ArrowRightIcon } from '@untitledui-icons/react/line'

interface BusinessTypeStepProps {
  onNext: () => void
}

export function BusinessTypeStep({ onNext }: BusinessTypeStepProps) {
  const { t } = useTranslation()
  const { businessType, setBusinessType } = useOnboardingStore()
  const [selected, setSelected] = useState<BusinessType | null>(businessType)

  // Sync local state with store when component mounts or businessType changes
  useEffect(() => {
    setSelected(businessType)
  }, [businessType])

  // Business type options with translations
  const businessOptions: Array<{
    value: BusinessType
    label: string
    description: string
  }> = [
    {
      value: 'RESTAURANT',
      label: t('resources.signup.business_type.restaurants'),
      description: t('resources.signup.business_type.restaurants_description'),
    },
    {
      value: 'HOTEL',
      label: t('resources.signup.business_type.hotels'),
      description: t('resources.signup.business_type.hotels_description'),
    },
    {
      value: 'SMALL_BUSINESS',
      label: t('resources.signup.business_type.smbs'),
      description: t('resources.signup.business_type.smbs_description'),
    },
  ]

  const handleContinue = () => {
    if (selected) {
      setBusinessType(selected)
      onNext()
    }
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-6">
      <StepHeader
        title={t('resources.signup.business_type.title')}
        subtitle={t('resources.signup.business_type.subtitle')}
        showBack={false}
      />

      {/* Radio options */}
      <div className="flex flex-col gap-4">
        {businessOptions.map((option) => (
          <RadioCard
            key={option.value}
            label={option.label}
            description={option.description}
            value={option.value}
            selected={selected === option.value}
            onChange={(v) => setSelected(v as BusinessType)}
          />
        ))}
      </div>

      <div className="mt-auto pt-4 lg:mt-0 lg:pt-0">
        <OnboardingButton
          onClick={handleContinue}
          isDisabled={!selected}
          iconTrailing={ArrowRightIcon}
        >
          {t('resources.signup.common.continue')}
        </OnboardingButton>
      </div>
    </div>
  )
}
